import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

// Import gallery images - all with transparent backgrounds
import praezisionsbauteil01 from '@/assets/gallery/praezisionsbauteil-01.png';
import flansch01 from '@/assets/gallery/flansch-01.png';
import halterung01 from '@/assets/gallery/halterung-01.png';
import halterung02 from '@/assets/gallery/halterung-02.png';
import kunststoff01 from '@/assets/gallery/kunststoff-01.png';
import stahl01 from '@/assets/gallery/stahl-01.png';
import aluKomplex01 from '@/assets/gallery/alu-komplex-01.png';
import winkel01 from '@/assets/gallery/winkel-01.png';
import gehaeuse01 from '@/assets/gallery/gehaeuse-01.png';
import messing01 from '@/assets/gallery/messing-01.png';
import winkel02 from '@/assets/gallery/winkel-02.png';
import kunststoffRot01 from '@/assets/gallery/kunststoff-rot-01.png';
import stahlblock01 from '@/assets/gallery/stahlblock-01.png';
import lagerblock01 from '@/assets/gallery/lagerblock-01.png';
import aluCarbon01 from '@/assets/gallery/alu-carbon-01.png';
import lagerbuchsen01 from '@/assets/gallery/lagerbuchsen-01.png';
import winkelSet01 from '@/assets/gallery/winkel-set-01.png';
import praezisionsteile01 from '@/assets/gallery/praezisionsteile-01.png';
import fuenfAchsBauteil01 from '@/assets/gallery/5achs-bauteil-01.png';
import tBauteil01 from '@/assets/gallery/t-bauteil-01.png';
import werkzeugblock01 from '@/assets/gallery/werkzeugblock-01.png';
import flachbauteil01 from '@/assets/gallery/flachbauteil-01.png';

interface GalleryItem {
  id: number;
  title: string;
  description: string;
  category: string;
  material: string;
  image: string;
}

const GallerySection = () => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const projects: GalleryItem[] = [
    {
      id: 1,
      title: 'CNC-Präzisionsplatte',
      description: 'Hochpräzise Aluminium-Platte mit komplexen Fräsungen und Bohrungen für den Maschinenbau.',
      category: 'CNC-Fräsen',
      material: 'Aluminium',
      image: praezisionsbauteil01,
    },
    {
      id: 2,
      title: 'Adapteraufnahme',
      description: 'Quadratischer Flansch mit präzisen Passbohrungen und Zentrierbohrung.',
      category: 'Frästeil',
      material: 'Aluminium',
      image: flansch01,
    },
    {
      id: 3,
      title: 'Schnellwechsler',
      description: 'Mehrachsig gefertigte Halterung mit anspruchsvoller Geometrie.',
      category: '5-Achs-Bearbeitung',
      material: 'Aluminium',
      image: halterung01,
    },
    {
      id: 4,
      title: 'Schnellwechsler Rückseite',
      description: 'Alternative Ansicht der komplexen Halterung',
      category: '5-Achs-Bearbeitung',
      material: 'Aluminium',
      image: halterung02,
    },
    {
      id: 5,
      title: 'Einlegehilfe',
      description: 'Präzisionsbauteil aus technischem Kunststoff für Spezialanwendungen.',
      category: 'Kunststoffbearbeitung',
      material: 'Kunststoff PE1000',
      image: kunststoff01,
    },
    {
      id: 6,
      title: 'Stahlbauteil',
      description: 'Gehärtetes Stahlbauteil mit präzisen Konturen und Passungen.',
      category: 'Stahlbearbeitung',
      material: 'Stahl',
      image: stahl01,
    },
    {
      id: 7,
      title: 'Winkelstütze',
      description: 'Aufwendig gefertigtes Bauteil mit mehreren Bearbeitungsebenen.',
      category: '5-Achs-Bearbeitung',
      material: 'Aluminium',
      image: aluKomplex01,
    },
    {
      id: 8,
      title: 'Winkelstück',
      description: 'Präzises Winkelstück mit komplexen Konturen für Maschinenbau.',
      category: 'CNC-Fräsen',
      material: 'Stahl',
      image: winkel01,
    },
    {
      id: 9,
      title: 'Monitoraufnahme',
      description: 'Gefräste Gehäuseplatte mit Aussparungen und Befestigungspunkten.',
      category: 'Kunststoffbearbeitung',
      material: 'PVC',
      image: gehaeuse01,
    },
    {
      id: 10,
      title: 'Gehäuse',
      description: 'Gehäuse aus Messing mit Innengewinde und Präzisionsbohrungen.',
      category: 'Frästeil',
      material: 'Messing',
      image: messing01,
    },
    {
      id: 11,
      title: 'Seitenschieber',
      description: 'Präzises Bauteil mit abgewinkelter Form und Bohrungsmuster.',
      category: '5-Achs-Bearbeitung',
      material: 'Aluminium',
      image: winkel02,
    },
    {
      id: 12,
      title: 'Schiebestück',
      description: 'Bauteil rot eloxiert',
      category: 'Aluminiumbearbeitung',
      material: 'Aluminium',
      image: kunststoffRot01,
    },
    {
      id: 13,
      title: 'Zentrierblock',
      description: 'Stahlblock mit Zentrierbohrungen',
      category: 'Stahlbearbeitung',
      material: 'Stahl',
      image: stahlblock01,
    },
    {
      id: 14,
      title: 'Lagerblock',
      description: 'Kompakter Lagerblock mit Präzisionspassungen für Wellenaufnahme.',
      category: 'CNC-Fräsen',
      material: 'Aluminium',
      image: lagerblock01,
    },
    {
      id: 15,
      title: 'Sensoraufnahme',
      description: 'Präzisionsbauteil aus Aluminium für Sensoranwendungen.',
      category: '5-Achs-Bearbeitung',
      material: 'Aluminium',
      image: aluCarbon01,
    },
    {
      id: 16,
      title: 'Lagerbuchsen-Set',
      description: 'Paar von Aluminium-Lagerbuchsen mit Präzisionspassungen.',
      category: 'Frästeile',
      material: 'Aluminium',
      image: lagerbuchsen01,
    },
    {
      id: 17,
      title: 'Winkel-Set',
      description: 'Set von Präzisions-Winkelstücken für den Maschinenbau.',
      category: '5-Achs-Bearbeitung',
      material: 'Stahl',
      image: winkelSet01,
    },
    {
      id: 18,
      title: 'Einlegenest',
      description: 'Bauteilablage für Roboter-Handlingmodul',
      category: 'CNC-Fräsen',
      material: 'Aluminium',
      image: praezisionsteile01,
    },
    {
      id: 19,
      title: 'Zylinderanbindung',
      description: 'Komplexes Bauteil gefertigt mit 2 Aufspannungen',
      category: '5-Achs-Bearbeitung',
      material: 'Aluminium',
      image: fuenfAchsBauteil01,
    },
    {
      id: 20,
      title: 'Seitenstütze',
      description: 'Präzises T-förmiges Bauteil mit hoher Oberflächengüte.',
      category: 'CNC-Fräsen',
      material: 'Aluminium',
      image: tBauteil01,
    },
    {
      id: 21,
      title: 'Werkzeugaufnahme',
      description: 'Werkzeugblock mit Frästasche und Befestigungsbohrungen.',
      category: '5-Achs-Bearbeitung',
      material: 'Aluminium',
      image: werkzeugblock01,
    },
    {
      id: 22,
      title: 'Flachschieber',
      description: 'Präzises Flachbauteil mit Langloch und Nut',
      category: 'CNC-Fräsen',
      material: 'Aluminium',
      image: flachbauteil01,
    },
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case '5-Achs-Bearbeitung':
        return 'bg-primary/90 text-primary-foreground';
      case 'CNC-Fräsen':
        return 'bg-fastlane/90 text-white';
      case 'Frästeil':
        return 'bg-smart/90 text-white';
      case 'Kunststoffbearbeitung':
        return 'bg-green-600/90 text-white';
      case 'Stahlbearbeitung':
        return 'bg-steel-dark/90 text-white';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const navigatePrev = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex((prev) => 
        prev !== null ? (prev === 0 ? projects.length - 1 : prev - 1) : null
      );
    }
  }, [selectedIndex, projects.length]);

  const navigateNext = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex((prev) => 
        prev !== null ? (prev === projects.length - 1 ? 0 : prev + 1) : null
      );
    }
  }, [selectedIndex, projects.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigatePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, navigatePrev, navigateNext]);

  const selectedProject = selectedIndex !== null ? projects[selectedIndex] : null;

  return (
    <section id="galerie" className="py-20 lg:py-32 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Unsere Projekte
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Präzision in Perfektion – Einblicke in unsere Fertigungsqualität
          </p>
        </motion.div>

        {/* Compact Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.03 }}
              className="group relative overflow-hidden rounded-xl bg-card border border-border cursor-pointer hover:border-primary/50 hover:shadow-lg transition-all duration-300"
              onClick={() => setSelectedIndex(index)}
            >
              {/* Image Container */}
              <div className="aspect-square overflow-hidden bg-muted flex items-center justify-center p-2">
                <img
                  src={project.image}
                  alt={`${project.title} – CNC-gefrästes Bauteil aus ${project.material}, gefertigt von SR-Zerspanung in Stamsried`}
                  className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                
                {/* Hover Zoom Icon */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Maximize2 className="w-4 h-4 text-foreground" />
                  </div>
                </div>
              </div>

              {/* Always Visible Info */}
              <div className="p-2 md:p-3 space-y-1">
                <Badge className={`text-[10px] md:text-xs px-1.5 py-0.5 ${getCategoryColor(project.category)}`}>
                  {project.category}
                </Badge>
                <h3 className="font-heading font-semibold text-xs md:text-sm text-foreground line-clamp-1">
                  {project.title}
                </h3>
                <p className="text-[10px] md:text-xs text-muted-foreground">
                  {project.material}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Lightbox Dialog */}
        <Dialog open={selectedIndex !== null} onOpenChange={(open) => !open && setSelectedIndex(null)}>
          <DialogContent className="max-w-4xl w-[95vw] p-0 bg-background/95 backdrop-blur-xl border-border overflow-hidden">
            <VisuallyHidden>
              <DialogTitle>{selectedProject?.title || 'Projektdetails'}</DialogTitle>
              <DialogDescription>{selectedProject?.description || 'Projektbeschreibung'}</DialogDescription>
            </VisuallyHidden>
            
            <AnimatePresence mode="wait">
              {selectedProject && (
                <motion.div
                  key={selectedProject.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="relative"
                >
                  {/* Image */}
                  <div className="relative aspect-square md:aspect-[4/3] overflow-hidden bg-muted flex items-center justify-center">
                    <img
                      src={selectedProject.image}
                      alt={`${selectedProject.title} – CNC-gefrästes Bauteil aus ${selectedProject.material}, gefertigt von SR-Zerspanung in Stamsried`}
                      className="max-w-full max-h-full object-contain p-4"
                    />
                  </div>

                  {/* Navigation Arrows */}
                  <button
                    onClick={(e) => { e.stopPropagation(); navigatePrev(); }}
                    className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    aria-label="Vorheriges Bild"
                  >
                    <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigateNext(); }}
                    className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    aria-label="Nächstes Bild"
                  >
                    <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                  </button>

                  {/* Info Panel */}
                  <div className="p-4 md:p-6 bg-background">
                    <div className="flex flex-wrap items-start gap-3 mb-3">
                      <Badge className={getCategoryColor(selectedProject.category)}>
                        {selectedProject.category}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Material: {selectedProject.material}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground mb-2">
                      {selectedProject.title}
                    </h3>
                    <p className="text-muted-foreground text-sm md:text-base">
                      {selectedProject.description}
                    </p>
                    
                    {/* Image Counter */}
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {selectedIndex !== null ? selectedIndex + 1 : 0} / {projects.length}
                      </span>
                      <span className="text-xs text-muted-foreground hidden md:block">
                        Pfeiltasten ← → zum Navigieren • ESC zum Schließen
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default GallerySection;
